import { create } from 'zustand';
import { Project, AppRow, Status, Phase, Artifact, AssemblyLine, AssemblyLineSummary, Question, ReviewComment, PublishedArtifactInfo, GuardrailResult, Attachment } from './types';
import { AssemblyLineDraft } from './lib/assemblyLineIo';
import {
  bootstrap as apiBootstrap,
  getProject as apiGetProject,
  listProjects as apiListProjects,
  runStep as apiRunStep,
  stopStep as apiStopStep,
  skipStep as apiSkipStep,
  editStep as apiEditStep,
  answerStep as apiAnswerStep,
  pinStep as apiPinStep,
  replaceArtifact as apiReplaceArtifact,
  openProjectStream,
  getDemoProject,
  listAssemblyLines as apiListAssemblyLines,
  getAssemblyLine as apiGetAssemblyLine,
  createAssemblyLine as apiCreateAssemblyLine,
  updateAssemblyLine as apiUpdateAssemblyLine,
  deleteAssemblyLine as apiDeleteAssemblyLine,
  getStepComments as apiGetStepComments,
  getPublishedDocs as apiGetPublishedDocs,
  resolveComments as apiResolveComments,
  ImpactRow,
} from './lib/api';
import {
  AuthUser,
  AuthEnterprise,
  Membership,
  fetchMe,
  switchWorkspace,
  login as apiLogin,
  logout as apiLogout,
  getToken,
} from './lib/auth';

export type Page = 'landing' | 'apps' | 'pipeline' | 'assemblyLines' | 'assemblyLine-edit';

interface UIState {
  /** Auth: the signed-in user + their workspace, or null. `authChecked`
   *  flips true once the initial /auth/me hydration has resolved (so guards
   *  don't flash). */
  currentUser: AuthUser | null;
  currentEnterprise: AuthEnterprise | null;
  authChecked: boolean;
  /** Workspaces the user belongs to (for the switcher). */
  userEnterprises: Membership[];
  /** The active workspace id (server-authoritative; mirrored from /auth/me). */
  activeEnterpriseId: string | null;
  /** Super-admins only: every workspace they may switch into. */
  allEnterprises: AuthEnterprise[];

  page: Page;
  project: Project | null;
  /**
   * Per-step active version overrides: { stepKey → projectVersionId }. Set
   * when the user switches a step to view a prior version via HistoryModal.
   * Drives the History button's visibility for overlaid steps (since their
   * status/version may not otherwise signal that history exists).
   */
  activeStepVersions: Record<string, string>;
  /** The current user's effective role on the loaded project. 'viewer' = read-only. */
  projectRole: 'owner' | 'editor' | 'viewer' | null;
  apps: AppRow[];

  /** index into project.phases */
  activePhaseIdx: number;
  /** phase keys that are expanded in the sidebar */
  expandedPhases: Set<string>;

  /** right panel state */
  rpOpen: boolean;
  rpStepKey: string | null;
  rpInitialTab: 'artifact' | 'reviews' | null;

  /** fullscreen overlay state */
  fsOpen: boolean;
  fsArtifactIdx: number;

  /** transient flags */
  bootstrapping: boolean;
  bootstrapError: string | null;
  /** Non-fatal notice from the last bootstrap (e.g. LLM API not configured). */
  bootstrapWarning: string | null;

  /** active SSE stream — only ever one at a time. */
  stream: EventSource | null;
  /**
   * Per-step token-cost pill, keyed by step key. Live-only (populated from SSE,
   * reset on project load) — the persisted fallback lives on the step itself
   * (`step.lastRunCostUsd`). `estimated` drives the "≈" marker (single-shot).
   */
  tokenPills: Record<string, { costUsd: number; versionId: string; estimated?: boolean }>;
  /** Per-step error message from the last `step.failed` event, keyed by step key. */
  stepErrors: Record<string, { message: string; kind?: string }>;
  /**
   * Per-step live retry status from `step.retrying` events, keyed by step key.
   * Present only while a step is actively retrying; cleared on start/done/failed.
   */
  stepRetries: Record<
    string,
    { kind: 'rate-limit' | 'validation'; attempt: number; max: number; reason: string; nextDelayMs?: number }
  >;
  /** Per-step start timestamp for live elapsed timer, keyed by step key. */
  stepTimers: Record<string, number>;
  /**
   * Latest live progress line from `step.progress` events (agentic/hybrid tool
   * activity + the `↳ ended:` line), keyed by step key. Cleared on
   * start/done/failed. Lets the UI show what a long agentic loop is doing
   * instead of a bare spinner.
   */
  stepProgress: Record<string, string>;

  /**
   * Latest downstream-outdated payload from the most recent Improve action.
   * Drives the optional summary banner showing which steps are now outdated.
   */
  lastOutdated: { rootStepKey: string; affected: ImpactRow[] } | null;
  /** Cascade timeline: most recent at the end. */
  cascadeLog: Array<{ ts: string; stepKey?: string; kind: string; note?: string }>;

  /** Published artifact docs (Confluence pages, etc.) keyed by step key */
  publishedDocs: Record<string, PublishedArtifactInfo[]>;
  /** Review comments from external platforms (Confluence, Notion, etc.) */
  stepComments: Record<string, ReviewComment[]>;

  /** Demo simulation state */
  demoSimRunning: boolean;
  /** Snapshot of demo project with all artifacts, used to restore step-by-step */
  demoSimSnapshot: Project | null;
  /** Time multiplier for demo: real elapsed × this = displayed duration */
  demoTimeScale: number;

  /** AssemblyLine state */
  assemblyLines: AssemblyLineSummary[];
  selectedAssemblyLineId: string | null;
  editingAssemblyLine: AssemblyLine | null;
  /**
   * A parsed-but-unsaved assemblyLine handed off from "Import assemblyLine". Kept
   * separate from `editingAssemblyLine` (which implies an existing server record);
   * the editor reads this to pre-fill a *new* assemblyLine, then clears it.
   */
  importedDraft: AssemblyLineDraft | null;

  /** Auth actions. */
  hydrateAuth: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Used by Register / AcceptInvite to set both the user and enterprise after a self-serve flow. */
  setSession: (user: AuthUser, enterprise: AuthEnterprise | null) => void;
  /** Switch the active workspace (server-side) and refresh all tenant data. */
  switchEnterprise: (enterpriseId: string) => Promise<void>;

  setPage: (p: Page) => void;
  openDemo: () => Promise<void>;
  bootstrapFromIdea: (idea: string, metadata: Record<string, string>, assemblyLineId?: string, appName?: string, selectedStepKeys?: string[], attachments?: Attachment[]) => Promise<void>;
  loadProject: (id: string) => Promise<void>;
  /**
   * Lightweight re-fetch of the loaded project's spec + active-version pointers
   * + review comments, without touching the SSE stream or the sidebar's
   * active/expanded phase state. Used after the Confluence connection changes
   * (top-level or in-step), when the server may have re-pinned step versions and
   * comments now resolve from a different profile.
   */
  refreshProjectData: (id: string) => Promise<void>;
  refreshApps: () => Promise<void>;
  togglePhaseExpand: (key: string) => void;
  setActivePhase: (idx: number) => void;
  showArtifact: (stepKey: string, tab?: 'artifact' | 'reviews') => void;
  hidePanel: () => void;
  openFullscreen: () => void;
  closeFullscreen: () => void;
  setFsArtifactIdx: (i: number) => void;
  /**
   * Record a pending edit prompt for a step. Once set, the step's "Re-run"
   * affordance becomes visible on its card and in the right-panel footer.
   */
  setPendingEdit: (stepKey: string, text: string) => void;
  rerunStep: (stepKey: string) => void;
  /** Ask the orchestrator to stop an in-flight run/retry for a step. */
  stopStep: (stepKey: string) => void;
  /**
   * Skip a step without running it. Marks it `skipped` (optimistically, then
   * confirmed by the `step.skipped` SSE event). Downstream steps that depend on
   * it become runnable but receive none of its output.
   */
  skipStep: (stepKey: string) => void;
  /**
   * Submit an edit to the orchestrator. The server stores the feedback, runs
   * impact analysis to mark downstream steps `outdatedReason`, and immediately
   * re-executes the root step with the feedback folded into its LLM prompt.
   */
  submitEdit: (stepKey: string, feedback: string, attachments?: Attachment[]) => Promise<void>;
  /**
   * Submit answers to the questions a step raised. Stamps answers onto the
   * local step state optimistically, POSTs to the orchestrator, and the
   * step.started → step.done SSE events drive the rest.
   */
  submitAnswers: (stepKey: string, answers: Record<string, string>, attachments?: Attachment[]) => Promise<void>;
  /** Dismiss the "N steps are now outdated" summary banner. */
  dismissOutdatedBanner: () => void;
  togglePin: (stepKey: string) => Promise<void>;
  /** Replace one artifact's content from an uploaded file (real projects only). */
  replaceArtifact: (stepKey: string, name: string, content: string) => Promise<void>;
  /** Close any open SSE stream. */
  closeStream: () => void;

  /** Review integration actions */
  fetchPublishedDocs: (stepKey: string) => Promise<void>;
  fetchComments: (stepKey: string) => Promise<void>;
  resolveComment: (stepKey: string, commentId: string) => Promise<void>;

  /** Demo simulation actions */
  startDemoSimulation: () => void;
  stopDemoSimulation: () => void;

  /** AssemblyLine actions */
  loadAssemblyLines: () => Promise<void>;
  selectAssemblyLine: (id: string | null) => void;
  loadAssemblyLineForEdit: (id: string) => Promise<void>;
  saveAssemblyLineToServer: (data: Partial<AssemblyLine> & { name: string }) => Promise<void>;
  updateAssemblyLineOnServer: (id: string, data: Partial<AssemblyLine> & { name: string }) => Promise<void>;
  deleteAssemblyLineFromServer: (id: string) => Promise<void>;
  clearEditingAssemblyLine: () => void;
  /** Stash a parsed assemblyLine draft for the editor to pick up as a new assemblyLine. */
  setImportedDraft: (draft: AssemblyLineDraft) => void;
  clearImportedDraft: () => void;
}

export const useStore = create<UIState>()((set, get) => ({
  currentUser: null,
  currentEnterprise: null,
  userEnterprises: [],
  activeEnterpriseId: null,
  allEnterprises: [],
  authChecked: false,
  page: 'landing',
  project: null,
  activeStepVersions: {},
  projectRole: null,
  apps: [],
  activePhaseIdx: 0,
  expandedPhases: new Set<string>(),
  rpOpen: false,
  rpStepKey: null,
  rpInitialTab: null,
  fsOpen: false,
  fsArtifactIdx: 0,
  bootstrapping: false,
  bootstrapError: null,
  bootstrapWarning: null,
  stream: null,
  tokenPills: {},
  stepErrors: {},
  stepRetries: {},
  stepTimers: {},
  stepProgress: {},
  lastOutdated: null,
  cascadeLog: [],
  publishedDocs: {},
  stepComments: {},
  demoSimRunning: false,
  demoSimSnapshot: null,
  demoTimeScale: 1,
  assemblyLines: [],
  selectedAssemblyLineId: null,
  editingAssemblyLine: null,
  importedDraft: null,

  hydrateAuth: async () => {
    if (!getToken()) {
      set({
        currentUser: null,
        currentEnterprise: null,
        userEnterprises: [],
        activeEnterpriseId: null,
        allEnterprises: [],
        authChecked: true,
      });
      return;
    }
    try {
      const me = await fetchMe();
      set({
        currentUser: me.user,
        currentEnterprise: me.activeEnterprise,
        userEnterprises: me.memberships,
        activeEnterpriseId: me.activeEnterpriseId,
        allEnterprises: me.allEnterprises ?? [],
        authChecked: true,
      });
    } catch {
      // Invalid/expired token — fetchMe / authedFetch already cleared it.
      set({
        currentUser: null,
        currentEnterprise: null,
        userEnterprises: [],
        activeEnterpriseId: null,
        allEnterprises: [],
        authChecked: true,
      });
    }
  },

  signIn: async (email, password) => {
    const user = await apiLogin(email, password);
    // Login response carries only the user + token; pull the full membership
    // picture from /auth/me so the switcher + header can render.
    let me: Awaited<ReturnType<typeof fetchMe>> | null = null;
    try {
      me = await fetchMe();
    } catch {
      /* tolerate transient hydration failure — the user is logged in */
    }
    // Wipe any data state from a previous session in the same tab — a fresh
    // login is conceptually a fresh tenant context.
    get().closeStream();
    set({
      currentUser: me?.user ?? user,
      currentEnterprise: me?.activeEnterprise ?? null,
      userEnterprises: me?.memberships ?? [],
      activeEnterpriseId: me?.activeEnterpriseId ?? null,
      allEnterprises: me?.allEnterprises ?? [],
      authChecked: true,
      ...freshDataState(),
    });
  },

  signOut: async () => {
    await apiLogout();
    get().closeStream();
    set({
      currentUser: null,
      currentEnterprise: null,
      userEnterprises: [],
      activeEnterpriseId: null,
      allEnterprises: [],
      ...freshDataState(),
    });
  },

  setSession: (user, enterprise) => {
    // Self-serve flows (register, accept-invite) reuse the existing tab — so
    // we have to drop any data state the previous session left behind. Same
    // reasoning as signIn above. Re-hydrate memberships from /auth/me in the
    // background so the switcher is populated.
    get().closeStream();
    set({
      currentUser: user,
      currentEnterprise: enterprise,
      activeEnterpriseId: enterprise?.id ?? null,
      authChecked: true,
      ...freshDataState(),
    });
    void get().hydrateAuth();
  },

  switchEnterprise: async (enterpriseId) => {
    const { activeEnterprise } = await switchWorkspace(enterpriseId);
    // Switching workspace is a fresh tenant context — wipe, then refetch
    // against the new workspace exactly like a fresh login. Without the
    // explicit reloads the apps/assembly-lines arrays stay empty until the next page
    // remount triggers its own fetch.
    get().closeStream();
    set({
      currentEnterprise: activeEnterprise,
      activeEnterpriseId: enterpriseId,
      ...freshDataState(),
    });
    // Repopulate memberships/active role, then the tenant data, against the new
    // workspace. Run concurrently — none depend on each other.
    await Promise.all([
      get().hydrateAuth(),
      get().refreshApps().catch(() => {}),
      get().loadAssemblyLines().catch(() => {}),
    ]);
  },

  setPage: (p) => set({ page: p }),

  closeStream: () => {
    const s = get().stream;
    if (s) {
      s.close();
      set({ stream: null });
    }
  },

  openDemo: async () => {
    try {
      const { project: proj, confluenceRefs } = await getDemoProject();
      get().closeStream();

      // Build demo apps list: the OKR Tracker (fully complete) plus a few
      // fictional apps at various pipeline stages to showcase the dashboard.
      const totalPhases = proj.phases.length; // 6
      const demoApps: AppRow[] = [
        {
          name: proj.appName || 'OKR Tracker',
          tag: 'Jira',
          phase: 'Content & SEO',
          phaseApp: 'success',
          done: totalPhases,
          total: totalPhases,
          date: new Date().toLocaleDateString(),
          assemblyLineName: 'Forge Pipeline',
          ownerLabel: 'Demo User',
        },
        {
          name: 'SignSeal',
          tag: 'Jira',
          phase: 'Design & Build App',
          phaseApp: 'inprogress',
          done: 2,
          total: totalPhases,
          date: new Date(Date.now() - 8 * 86400000).toLocaleDateString(),
          assemblyLineName: 'Forge Pipeline',
          ownerLabel: 'Demo User',
        },
        {
          name: 'Sprint Health Monitor',
          tag: 'Jira',
          phase: 'Design & Build',
          phaseApp: 'inprogress',
          done: 2,
          total: totalPhases,
          date: new Date(Date.now() - 2 * 86400000).toLocaleDateString(),
          assemblyLineName: 'Forge Pipeline',
          ownerLabel: 'Demo User',
        },
        {
          name: 'Confluence Page Insights',
          tag: 'Confluence',
          phase: 'Discovery',
          phaseApp: 'inprogress',
          done: 1,
          total: totalPhases,
          date: new Date(Date.now() - 5 * 86400000).toLocaleDateString(),
          assemblyLineName: 'Forge Pipeline',
          ownerLabel: 'Demo User',
        },
        {
          name: 'Release Notes Generator',
          tag: 'Jira',
          phase: 'Brand',
          phaseApp: 'inprogress',
          done: 4,
          total: totalPhases,
          date: new Date(Date.now() - 1 * 86400000).toLocaleDateString(),
          assemblyLineName: 'Forge Pipeline',
          ownerLabel: 'Demo User',
        },
        {
          name: 'Team Capacity Planner',
          tag: 'Jira',
          phase: 'Listing & Conversion',
          phaseApp: 'inprogress',
          done: 5,
          total: totalPhases,
          date: new Date(Date.now() - 3 * 86400000).toLocaleDateString(),
          assemblyLineName: 'Forge Pipeline',
          ownerLabel: 'Demo User',
        },
      ];

      // Demo assemblyLines — the 3 pipelines shown on the Landing page
      const demoAssemblyLines: AssemblyLineSummary[] = [
        {
          id: 'demo-forge-launchpad',
          name: 'Forge Launchpad',
          slug: 'forge-launchpad',
          description: 'From idea to live Marketplace Forge app — research, code, security, branding, listing, and launch campaign.',
          version: 1,
          phaseCount: 6,
          stepCount: 20,
        },
        {
          id: 'demo-app-accelerator',
          name: 'App Accelerator',
          slug: 'app-accelerator',
          description: 'Your existing app is plateauing. Competitive teardown, feature gaps, rewritten listing, and a relaunch playbook.',
          version: 1,
          phaseCount: 4,
          stepCount: 12,
        },
        {
          id: 'demo-dc-to-forge',
          name: 'DC → Forge Factory',
          slug: 'dc-to-forge',
          description: 'Data Center app with a migration deadline. Architecture mapping, Forge rebuild, compliance validation, and relaunch.',
          version: 1,
          phaseCount: 5,
          stepCount: 15,
        },
      ];

      set({
        project: proj,
        apps: demoApps,
        assemblyLines: demoAssemblyLines,
        selectedAssemblyLineId: demoAssemblyLines[0].id,
        activePhaseIdx: 0,
        expandedPhases: new Set(proj.phases.map((p) => p.key)),
        rpOpen: false,
        rpStepKey: null,
        rpInitialTab: null,
        publishedDocs: confluenceRefs ?? {},
      });

      // Fetch Confluence published docs and comments for all done steps
      const doneSteps = proj.phases.flatMap((ph) =>
        ph.steps.filter((s) => s.status === 'done'),
      );
      for (const s of doneSteps) {
        void get().fetchPublishedDocs(s.key);
        void get().fetchComments(s.key);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('openDemo failed', e);
      set({ bootstrapError: 'demo unavailable — is the orchestrator running?' });
    }
  },

  bootstrapFromIdea: async (idea, metadata, assemblyLineId, appName, selectedStepKeys, attachments) => {
    set({ bootstrapping: true, bootstrapError: null, bootstrapWarning: null });
    try {
      const { project, warning } = await apiBootstrap({ idea, metadata, assemblyLineId, appName, selectedStepKeys, attachments });
      get().closeStream();
      set({
        project,
        activePhaseIdx: 0,
        expandedPhases: new Set(project.phases.map((p) => p.key)),
        rpOpen: false,
        rpStepKey: null,
        bootstrapping: false,
        bootstrapWarning: warning ?? null,
        tokenPills: {},
        stepErrors: {},
      });
      attachStream(project.id, set, get);
    } catch (e: any) {
      set({ bootstrapping: false, bootstrapError: e?.message ?? 'bootstrap failed' });
    }
  },

  loadProject: async (id) => {
    const { project, activeStepVersions, role } = await apiGetProject(id);
    get().closeStream();
    // Auto-resume: find the first phase with a non-done step
    const resumeIdx = project.phases.findIndex((ph) =>
      ph.steps.some((s) => s.status !== 'done' && s.status !== 'skipped'),
    );
    const activeIdx = resumeIdx >= 0 ? resumeIdx : 0;
    set({
      project,
      activeStepVersions,
      projectRole: role,
      activePhaseIdx: activeIdx,
      expandedPhases: new Set(
        resumeIdx >= 0
          ? [project.phases[resumeIdx].key]
          : project.phases.map((p) => p.key),
      ),
      rpOpen: false,
      rpStepKey: null,
      tokenPills: {},
      stepErrors: {},
    });
    attachStream(project.id, set, get);

    // Proactively fetch review comments for all done steps so badges show immediately
    const doneSteps = project.phases.flatMap((ph) =>
      ph.steps.filter((s) => s.status === 'done'),
    );
    for (const s of doneSteps) {
      void get().fetchComments(s.key);
    }
  },

  refreshProjectData: async (id) => {
    try {
      const { project, activeStepVersions, role } = await apiGetProject(id);
      set({ project, activeStepVersions, projectRole: role });
      // Re-fetch comments + published-doc links for done steps so review panels
      // and "View in Confluence" links reflect the (possibly now different)
      // profile the steps resolve to.
      const doneSteps = project.phases.flatMap((ph) =>
        ph.steps.filter((s) => s.status === 'done'),
      );
      for (const s of doneSteps) {
        void get().fetchComments(s.key);
        void get().fetchPublishedDocs(s.key);
      }
    } catch {
      // Non-critical — leave current state; a full reload will reconcile.
    }
  },

  refreshApps: async () => {
    try {
      const list = await apiListProjects();
      const apps: AppRow[] = list.map((p) => {
        const total = p.totalPhases ?? 0;
        const done = p.donePhases ?? 0;
        const allDone = total > 0 && done >= total;
        return {
          id: p.id,
          name: p.appName,
          tag: tagFromMetadata(p.metadata),
          phase: allDone ? 'Completed' : (p.currentPhase ?? 'Discovery'),
          phaseApp: allDone ? 'success' as const : 'inprogress' as const,
          done,
          total,
          date: new Date(p.updatedAt).toLocaleDateString(),
          assemblyLineName: p.assemblyLineName ?? undefined,
          ownerUserId: p.ownerUserId,
          ownerLabel: p.ownerName?.trim() || p.ownerEmail?.split('@')[0] || undefined,
          role: p.role,
          isShared: p.isShared,
        };
      });
      set({ apps });
    } catch {
      // keep existing apps on failure
    }
  },

  togglePhaseExpand: (key) => {
    const set2 = new Set(get().expandedPhases);
    if (set2.has(key)) set2.delete(key);
    else set2.add(key);
    set({ expandedPhases: set2 });
  },

  setActivePhase: (idx) => set({ activePhaseIdx: idx }),

  showArtifact: (stepKey, tab) => {
    const s = get();
    // Toggle closed if clicking the same step with no specific tab request
    if (s.rpOpen && s.rpStepKey === stepKey && !tab) {
      set({ rpOpen: false });
    } else {
      set({ rpOpen: true, rpStepKey: stepKey, fsArtifactIdx: 0, rpInitialTab: tab ?? null });
    }
  },

  hidePanel: () => set({ rpOpen: false }),

  openFullscreen: () => set({ fsOpen: true }),
  closeFullscreen: () => set({ fsOpen: false }),
  setFsArtifactIdx: (i) => set({ fsArtifactIdx: i }),

  setPendingEdit: (stepKey, text) => {
    const proj = get().project;
    if (!proj) return;
    const phases: Phase[] = proj.phases.map((ph) => ({
      ...ph,
      steps: ph.steps.map((s) =>
        s.key === stepKey ? { ...s, pendingEdit: text || undefined } : s
      ),
    }));
    set({ project: { ...proj, phases } });
  },

  rerunStep: (stepKey) => {
    const proj = get().project;
    if (!proj) return;
    if (get().projectRole === 'viewer') return; // read-only collaborator
    // For the offline demo fixture (no orchestrator), simulate.
    if (!proj.id.startsWith('p_')) {
      const tx = (status: Status): Project => ({
        ...proj,
        phases: proj.phases.map((ph) => ({
          ...ph,
          steps: ph.steps.map((s) =>
            s.key === stepKey ? { ...s, status, pendingEdit: undefined } : s,
          ),
        })),
      });
      set({ project: tx('in-progress') });
      setTimeout(() => set({ project: tx('done') }), 900);
      return;
    }
    // Real path: optimistic mark + POST. SSE drives the rest.
    const nextErrors = { ...get().stepErrors };
    delete nextErrors[stepKey];
    set({
      project: {
        ...proj,
        phases: proj.phases.map((ph) => ({
          ...ph,
          steps: ph.steps.map((s) =>
            s.key === stepKey
              ? {
                  ...s,
                  status: 'in-progress',
                  pendingEdit: undefined,
                  outdatedReason: undefined,
                  lastError: undefined,
                }
              : s,
          ),
        })),
      },
      stepErrors: nextErrors,
      stepTimers: { ...get().stepTimers, [stepKey]: Date.now() },
    });
    void apiRunStep(proj.id, stepKey).catch((e) => {
      // eslint-disable-next-line no-console
      console.error('runStep failed', e);
    });
    // Safety net: if SSE drops the step.done event for any reason (stale tab,
    // EventSource still connecting, dev-server restart), poll the project
    // state until the step is no longer in-progress.
    startStepPolling(proj.id, stepKey, set, get);
  },

  stopStep: (stepKey) => {
    const proj = get().project;
    if (!proj || !proj.id.startsWith('p_')) return;
    if (get().projectRole === 'viewer') return; // read-only collaborator
    void apiStopStep(proj.id, stepKey).catch((e) => {
      // eslint-disable-next-line no-console
      console.error('stopStep failed', e);
    });
    // Don't optimistically flip status — the run continues until the next retry
    // boundary, then the server emits step.failed (kind:'stopped') which drives
    // the UI. Clearing the live retry pill gives immediate feedback that the
    // stop was requested.
    const retries = { ...get().stepRetries };
    delete retries[stepKey];
    set({ stepRetries: retries });
  },

  skipStep: (stepKey) => {
    const proj = get().project;
    if (!proj) return;
    if (get().projectRole === 'viewer') return; // read-only collaborator
    // Snapshot the pre-skip step so we can roll back if the server rejects the
    // skip (e.g. a 409 from a race where SSE flips the step to in-progress just
    // before the click) — skip is a state-mutating action where a silent
    // divergence between UI and server is more confusing than a failed run.
    const prevStep = proj.phases.flatMap((ph) => ph.steps).find((s) => s.key === stepKey);
    // Optimistically mark skipped and clear any transient run state. For the
    // offline demo fixture (no orchestrator) this is the whole story.
    const nextErrors = { ...get().stepErrors };
    delete nextErrors[stepKey];
    const retries = { ...get().stepRetries };
    delete retries[stepKey];
    set({
      project: {
        ...proj,
        phases: proj.phases.map((ph) => ({
          ...ph,
          steps: ph.steps.map((s) =>
            s.key === stepKey
              ? {
                  ...s,
                  status: 'skipped' as Status,
                  pendingQuestions: undefined,
                  pendingEdit: undefined,
                  lastError: undefined,
                  guardrailResults: undefined,
                }
              : s,
          ),
        })),
      },
      stepErrors: nextErrors,
      stepRetries: retries,
    });
    if (!proj.id.startsWith('p_')) return; // demo fixture — no server call
    void apiSkipStep(proj.id, stepKey).catch((e: unknown) => {
      // eslint-disable-next-line no-console
      console.error('skipStep failed', e);
      // Roll back the optimistic skip and surface the error so the UI doesn't
      // show "skipped" while the server didn't skip.
      if (prevStep) mutateStep(set, get, stepKey, () => prevStep);
      const message = e instanceof Error ? e.message : 'Failed to skip step';
      set({ stepErrors: { ...get().stepErrors, [stepKey]: { message } } });
    });
  },

  submitAnswers: async (stepKey, answers, attachments) => {
    const proj = get().project;
    if (!proj || !proj.id.startsWith('p_')) return;
    if (get().projectRole === 'viewer') return; // read-only collaborator
    // Optimistically stamp answers + transition to in-progress. The server
    // will save its own version; we don't wait for it to flip the UI.
    set({
      project: {
        ...proj,
        phases: proj.phases.map((ph) => ({
          ...ph,
          steps: ph.steps.map((s) => {
            if (s.key !== stepKey) return s;
            const stamped: Question[] | undefined = s.pendingQuestions?.map((q) =>
              answers[q.id] ? { ...q, answer: answers[q.id] } : q,
            );
            return {
              ...s,
              status: 'in-progress' as Status,
              pendingQuestions: stamped,
              lastError: undefined,
            };
          }),
        })),
      },
    });
    try {
      await apiAnswerStep(proj.id, stepKey, answers, attachments);
      // The /answer route triggers executeStep; SSE will flip status to done
      // (or back to awaiting-input if the LLM asks follow-ups).
      startStepPolling(proj.id, stepKey, set, get);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('submitAnswers failed', e);
    }
  },

  submitEdit: async (stepKey, feedback, attachments) => {
    const proj = get().project;
    if (!proj || !proj.id.startsWith('p_')) return;
    if (get().projectRole === 'viewer') return; // read-only collaborator
    // Snapshot the step so we can roll back the optimistic flip if the request
    // is rejected (otherwise a failed edit would leave the step stuck showing
    // "in-progress" forever, with no error surfaced).
    const prevStep = proj.phases.flatMap((ph) => ph.steps).find((s) => s.key === stepKey);
    // Optimistic UI: stamp pendingEdit and flip status to in-progress. The
    // server-side PATCH /edit applies impact + re-executes immediately, and
    // SSE will drive the rest.
    const nextErrors = { ...get().stepErrors };
    delete nextErrors[stepKey];
    set({
      project: {
        ...proj,
        phases: proj.phases.map((ph) => ({
          ...ph,
          steps: ph.steps.map((s) =>
            s.key === stepKey
              ? { ...s, pendingEdit: feedback, status: 'in-progress' as Status, lastError: undefined }
              : s,
          ),
        })),
      },
      stepErrors: nextErrors,
    });
    try {
      await apiEditStep(proj.id, stepKey, feedback, attachments);
      startStepPolling(proj.id, stepKey, set, get);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('submitEdit failed', e);
      // Roll back to the pre-edit status and surface the failure so the step
      // doesn't sit at "in-progress" with the error swallowed.
      const cur = get().project;
      if (cur && cur.id === proj.id) {
        set({
          project: {
            ...cur,
            phases: cur.phases.map((ph) => ({
              ...ph,
              steps: ph.steps.map((s) =>
                s.key === stepKey
                  ? { ...s, status: (prevStep?.status ?? 'done') as Status, pendingEdit: prevStep?.pendingEdit }
                  : s,
              ),
            })),
          },
          stepErrors: {
            ...get().stepErrors,
            [stepKey]: { message: (e as Error)?.message ?? 'Edit failed' },
          },
        });
      }
    }
  },

  dismissOutdatedBanner: () => set({ lastOutdated: null }),

  fetchPublishedDocs: async (stepKey) => {
    const proj = get().project;
    if (!proj) return;
    const apiId = proj.id.startsWith('p_') ? proj.id : 'demo';
    try {
      const { docs } = await apiGetPublishedDocs(apiId, stepKey);
      if (docs.length > 0) {
        set((s) => ({
          publishedDocs: { ...s.publishedDocs, [stepKey]: docs },
        }));
      }
    } catch {
      // Non-critical
    }
  },

  fetchComments: async (stepKey) => {
    const proj = get().project;
    if (!proj) return;
    // Demo projects use 'demo' as the API project ID (routes are under /projects/demo/)
    const apiId = proj.id.startsWith('p_') ? proj.id : 'demo';
    try {
      const comments = await apiGetStepComments(apiId, stepKey);
      set((s) => ({
        stepComments: { ...s.stepComments, [stepKey]: comments },
      }));
    } catch {
      // Non-critical — review integration may not be active
    }
  },

  resolveComment: async (stepKey, commentId) => {
    const proj = get().project;
    if (!proj || !proj.id.startsWith('p_')) return;
    const comments = get().stepComments[stepKey];
    const comment = comments?.find((c) => c.id === commentId);
    if (!comment) return;
    const feedback = `[Review feedback] ${comment.body}`;
    await get().submitEdit(stepKey, feedback);
  },

  /* ---------------------------------------------------------------- */
  /*  Demo pipeline simulation                                        */
  /* ---------------------------------------------------------------- */

  startDemoSimulation: () => {
    const project = get().project;
    if (!project || get().demoSimRunning) return;

    // Use existing snapshot if available (set by resetProjectToPending during tour),
    // otherwise snapshot the current project
    const snapshot = get().demoSimSnapshot
      ?? JSON.parse(JSON.stringify(project)) as Project;

    // Reset all steps to pending, clear artifacts
    const resetProject: Project = {
      ...project,
      version: 0,
      phases: project.phases.map((ph) => ({
        ...ph,
        steps: ph.steps.map((s) => ({
          ...s,
          status: 'pending' as Status,
          artifacts: [],
          version: 0,
          totalTimeMs: undefined,
          lastRunAt: undefined,
          lastRunDurationMs: undefined,
        })),
      })),
    };

    let cancelled = false;
    set({
      project: resetProject,
      demoSimRunning: true,
      demoSimSnapshot: snapshot,
      demoTimeScale: 1, // updated below after allSteps is built
      activePhaseIdx: 0,
      expandedPhases: new Set(project.phases.map((p) => p.key)),
      tokenPills: {},
      stepErrors: {},
      stepTimers: {},
      rpOpen: false,
      rpStepKey: null,
      rpInitialTab: null,
    });

    // Step timing map (ms) — tuned so the full pipeline completes in ~12s
    const stepDelay: Record<string, number> = {
      'mkt-research': 850, 'mkt-brand-voice': 550, 'mkt-positioning': 550,
      analyze: 700, layout: 850, generate: 1100, deploy: 400,
      'security-audit': 550, 'product-docs': 850,
      'generate-logo': 550, 'mkt-creative-strategist': 400,
      'mkt-product-photo': 400, 'mkt-image-gen': 400, 'mkt-social-graphics': 400,
      'mkt-keyword-research': 400, 'mkt-direct-response': 550,
      'mkt-frontend-design': 700, 'mkt-lead-magnet': 400,
      'mkt-seo-content': 550, 'mkt-content-atomizer': 400, 'mkt-newsletter': 400,
    };
    const defaultDelay = 550;

    // Walk through all phases and steps sequentially
    const allSteps: Array<{ phaseIdx: number; stepKey: string; delay: number }> = [];
    snapshot.phases.forEach((ph, phaseIdx) => {
      ph.steps.forEach((s) => {
        allSteps.push({ phaseIdx, stepKey: s.key, delay: stepDelay[s.key] ?? defaultDelay });
      });
    });

    // Time scaling: 17s real time should display as 2 hours
    const totalRealMs = allSteps.reduce((sum, s) => sum + s.delay, 0);
    const targetDisplayMs = (2 * 60 + 12) * 60 * 1000; // 2h 12m
    const timeScale = targetDisplayMs / totalRealMs;
    set({ demoTimeScale: timeScale });

    (async () => {
      for (const { phaseIdx, stepKey, delay } of allSteps) {
        if (cancelled) return;

        // Advance to this step's phase
        set({ activePhaseIdx: phaseIdx });

        // Mark step as in-progress
        const startMs = Date.now();
        set((s) => ({
          stepTimers: { ...s.stepTimers, [stepKey]: startMs },
          project: s.project ? {
            ...s.project,
            phases: s.project.phases.map((ph) => ({
              ...ph,
              steps: ph.steps.map((st) =>
                st.key === stepKey ? { ...st, status: 'in-progress' as Status } : st,
              ),
            })),
          } : null,
        }));

        // Wait for the simulated duration
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, delay);
          // Check cancel flag periodically
          const check = setInterval(() => {
            if (cancelled) { clearTimeout(timer); clearInterval(check); resolve(); }
          }, 100);
          setTimeout(() => clearInterval(check), delay + 200);
        });
        if (cancelled) return;

        // Find the original artifacts from snapshot
        const snapStep = snapshot.phases
          .flatMap((ph) => ph.steps)
          .find((s) => s.key === stepKey);
        const displayDurationMs = Math.round(delay * timeScale);

        // Mark step as done with restored artifacts and scaled duration
        set((s) => {
          const timers = { ...s.stepTimers };
          delete timers[stepKey];
          return {
            stepTimers: timers,
            project: s.project ? {
              ...s.project,
              phases: s.project.phases.map((ph) => ({
                ...ph,
                steps: ph.steps.map((st) =>
                  st.key === stepKey ? {
                    ...st,
                    status: 'done' as Status,
                    artifacts: snapStep?.artifacts ?? [],
                    version: (st.version || 0) + 1,
                    lastRunDurationMs: displayDurationMs,
                    totalTimeMs: displayDurationMs,
                    lastRunAt: new Date().toISOString(),
                  } : st,
                ),
              })),
            } : null,
          };
        });
      }

      // Simulation complete
      set({ demoSimRunning: false });
    })();

    // Store cancel function
    set({
      // @ts-expect-error — we need to store cancel but can't set it atomically with the above
      _demoSimCancel: () => { cancelled = true; },
    });
  },

  stopDemoSimulation: () => {
    // Trigger cancel
    const cancel = (get() as any)._demoSimCancel;
    if (cancel) cancel();

    // Restore the full snapshot
    const snapshot = get().demoSimSnapshot;
    if (snapshot) {
      set({
        project: snapshot,
        demoSimRunning: false,
        demoSimSnapshot: null,
        stepTimers: {},
        activePhaseIdx: 0,
        expandedPhases: new Set(snapshot.phases.map((p) => p.key)),
      });
    } else {
      set({ demoSimRunning: false });
    }
  },

  loadAssemblyLines: async () => {
    try {
      const assemblyLines = await apiListAssemblyLines();
      set({ assemblyLines });
    } catch {
      // keep existing on failure
    }
  },

  selectAssemblyLine: (id) => set({ selectedAssemblyLineId: id }),

  loadAssemblyLineForEdit: async (id) => {
    const assemblyLine = await apiGetAssemblyLine(id);
    set({ editingAssemblyLine: assemblyLine });
  },

  saveAssemblyLineToServer: async (data) => {
    await apiCreateAssemblyLine(data);
    await get().loadAssemblyLines();
    // navigation handled by component
  },

  updateAssemblyLineOnServer: async (id, data) => {
    await apiUpdateAssemblyLine(id, data);
    await get().loadAssemblyLines();
    set({ editingAssemblyLine: null });
  },

  deleteAssemblyLineFromServer: async (id) => {
    await apiDeleteAssemblyLine(id);
    await get().loadAssemblyLines();
  },

  clearEditingAssemblyLine: () => set({ editingAssemblyLine: null }),

  setImportedDraft: (draft) => set({ importedDraft: draft, editingAssemblyLine: null }),

  clearImportedDraft: () => set({ importedDraft: null }),

  togglePin: async (stepKey) => {
    const proj = get().project;
    if (!proj || !proj.id.startsWith('p_')) return;
    if (get().projectRole === 'viewer') return; // read-only collaborator
    const step = proj.phases.flatMap((p) => p.steps).find((s) => s.key === stepKey);
    if (!step) return;
    const willPin = step.pinnedAtVersion == null;
    try {
      await apiPinStep(proj.id, stepKey, willPin);
      // optimistic local update; SSE step.pinned will re-confirm
      set({
        project: {
          ...proj,
          phases: proj.phases.map((ph) => ({
            ...ph,
            steps: ph.steps.map((s) =>
              s.key === stepKey
                ? { ...s, pinnedAtVersion: willPin ? s.version : undefined }
                : s,
            ),
          })),
        },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('togglePin failed', e);
    }
  },

  replaceArtifact: async (stepKey, name, content) => {
    const proj = get().project;
    if (!proj || !proj.id.startsWith('p_')) return;
    if (get().projectRole === 'viewer') return; // read-only collaborator
    const updated = await apiReplaceArtifact(proj.id, stepKey, name, content);
    // The server upserts the artifact and may promote a not-yet-run step to
    // done. Patch the step's artifacts/status/version from the response so the
    // open panel keeps its context without a full reload.
    const newStep = updated.phases.flatMap((p) => p.steps).find((s) => s.key === stepKey);
    if (!newStep) return;
    mutateStep(set, get, stepKey, (s) => ({
      ...s,
      artifacts: newStep.artifacts,
      status: newStep.status,
      version: newStep.version,
    }));
  },
}));

function attachStream(
  projectId: string,
  set: (partial: Partial<UIState> | ((s: UIState) => Partial<UIState>)) => void,
  get: () => UIState,
): void {
  const es = openProjectStream(projectId);

  es.addEventListener('step.started', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as { stepKey: string };
    mutateStep(set, get, data.stepKey, (s) => ({ ...s, status: 'in-progress' }));
    const retries = { ...get().stepRetries };
    delete retries[data.stepKey];
    const progress = { ...get().stepProgress };
    delete progress[data.stepKey];
    set({
      stepTimers: { ...get().stepTimers, [data.stepKey]: Date.now() },
      stepRetries: retries,
      stepProgress: progress,
    });
  });

  // Live agentic/hybrid tool activity (and the `↳ ended:` line). Keep only the
  // latest line per step; the step card renders it under the running spinner.
  es.addEventListener('step.progress', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as { stepKey: string; line: string };
    if (!data.line) return;
    set({ stepProgress: { ...get().stepProgress, [data.stepKey]: data.line } });
  });

  es.addEventListener('step.retrying', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as {
      stepKey: string;
      kind: 'rate-limit' | 'validation';
      attempt: number;
      max: number;
      reason: string;
      nextDelayMs?: number;
    };
    set({
      stepRetries: {
        ...get().stepRetries,
        [data.stepKey]: {
          kind: data.kind,
          attempt: data.attempt,
          max: data.max,
          reason: data.reason,
          nextDelayMs: data.nextDelayMs,
        },
      },
    });
  });

  es.addEventListener('artifact.delta', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as {
      stepKey: string;
      artifactIdx: number;
      name: string;
      appendText: string;
    };
    mutateStep(set, get, data.stepKey, (s) => {
      const artifacts = [...s.artifacts];
      const existing = artifacts[data.artifactIdx];
      const next: Artifact = existing
        ? { ...existing, content: existing.content + data.appendText }
        : { name: data.name, content: data.appendText };
      artifacts[data.artifactIdx] = next;
      return { ...s, artifacts };
    });
  });

  es.addEventListener('step.done', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as {
      stepKey: string;
      artifacts: Artifact[];
      versionId: string;
      costUsd: number;
      costEstimated?: boolean;
      durationMs?: number;
      guardrails?: GuardrailResult[];
    };
    mutateStep(set, get, data.stepKey, (s) => ({
      ...s,
      artifacts: data.artifacts,
      status: 'done',
      version: s.version + 1,
      pendingEdit: undefined,
      outdatedReason: undefined,
      guardrailResults: data.guardrails,
      totalTimeMs: (s.totalTimeMs ?? 0) + (data.durationMs ?? 0),
      lastRunAt: new Date().toISOString(),
      lastRunDurationMs: data.durationMs,
      lastRunCostUsd: data.costUsd,
      totalCostUsd: (s.totalCostUsd ?? 0) + data.costUsd,
      lastRunCostEstimated: data.costEstimated,
    }));
    const timers = { ...get().stepTimers };
    delete timers[data.stepKey];
    const pills = { ...get().tokenPills, [data.stepKey]: { costUsd: data.costUsd, versionId: data.versionId, estimated: data.costEstimated } };
    const clearedErrors = { ...get().stepErrors };
    delete clearedErrors[data.stepKey];
    const clearedRetries = { ...get().stepRetries };
    delete clearedRetries[data.stepKey];
    const clearedProgress = { ...get().stepProgress };
    delete clearedProgress[data.stepKey];
    set({ tokenPills: pills, stepErrors: clearedErrors, stepRetries: clearedRetries, stepTimers: timers, stepProgress: clearedProgress });

    // Fetch review comments for the completed step (artifacts just got published)
    void get().fetchComments(data.stepKey);
  });

  es.addEventListener('step.awaitingInput', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as {
      stepKey: string;
      questions: Question[];
      artifacts: Artifact[];
      versionId: string;
      costUsd: number;
      costEstimated?: boolean;
    };
    mutateStep(set, get, data.stepKey, (s) => ({
      ...s,
      status: 'awaiting-input',
      pendingQuestions: data.questions,
      artifacts: data.artifacts,
      lastRunCostUsd: data.costUsd,
      totalCostUsd: (s.totalCostUsd ?? 0) + data.costUsd,
      lastRunCostEstimated: data.costEstimated,
    }));
    const pills = { ...get().tokenPills, [data.stepKey]: { costUsd: data.costUsd, versionId: data.versionId, estimated: data.costEstimated } };
    set({ tokenPills: pills });
  });

  es.addEventListener('step.failed', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as { stepKey: string; error: string; kind?: string; durationMs?: number; costUsd?: number; costEstimated?: boolean; guardrails?: GuardrailResult[] };
    mutateStep(set, get, data.stepKey, (s) => ({
      ...s,
      status: 'failed',
      guardrailResults: data.guardrails ?? s.guardrailResults,
      totalTimeMs: (s.totalTimeMs ?? 0) + (data.durationMs ?? 0),
      lastRunAt: new Date().toISOString(),
      lastRunDurationMs: data.durationMs,
      lastRunCostUsd: data.costUsd ?? s.lastRunCostUsd,
      totalCostUsd: (s.totalCostUsd ?? 0) + (data.costUsd ?? 0),
      lastRunCostEstimated: data.costUsd != null ? data.costEstimated : s.lastRunCostEstimated,
    }));
    const timers = { ...get().stepTimers };
    delete timers[data.stepKey];
    const clearedRetries = { ...get().stepRetries };
    delete clearedRetries[data.stepKey];
    const clearedProgress = { ...get().stepProgress };
    delete clearedProgress[data.stepKey];
    set({
      stepErrors: {
        ...get().stepErrors,
        [data.stepKey]: { message: data.error, kind: data.kind },
      },
      stepRetries: clearedRetries,
      stepTimers: timers,
      stepProgress: clearedProgress,
    });
    // eslint-disable-next-line no-console
    console.warn(`[${data.stepKey}] ${data.kind === 'stopped' ? 'stopped' : 'failed'}:`, data.error);
  });

  es.addEventListener('attachment.error', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as {
      stepKey: string;
      requested: number;
      saved: number;
      error?: string;
    };
    // Surface a save failure on the step so the run doesn't appear to have used
    // files it silently dropped.
    set({
      stepErrors: {
        ...get().stepErrors,
        [data.stepKey]: {
          message:
            data.error ??
            `${data.requested - data.saved} of ${data.requested} attachment(s) could not be saved`,
          kind: 'attachment',
        },
      },
    });
    // eslint-disable-next-line no-console
    console.warn(`[${data.stepKey}] attachment save issue:`, data.error, `${data.saved}/${data.requested}`);
  });

  es.addEventListener('downstream.outdated', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as { rootStepKey: string; affected: ImpactRow[] };
    const proj = get().project;
    if (!proj) {
      set({ lastOutdated: data });
      return;
    }
    const rootLabel =
      proj.phases.flatMap((p) => p.steps).find((s) => s.key === data.rootStepKey)?.label ??
      data.rootStepKey;
    const reason = `Upstream changed: ${rootLabel}`;
    const impacted = new Set(
      data.affected.filter((a) => a.severity !== 'none').map((a) => a.stepKey),
    );
    set({
      lastOutdated: data,
      project: {
        ...proj,
        phases: proj.phases.map((ph) => ({
          ...ph,
          steps: ph.steps.map((s) =>
            impacted.has(s.key) ? { ...s, outdatedReason: reason } : s,
          ),
        })),
      },
    });
  });

  es.addEventListener('impact.failed', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as { rootStepKey: string; error: string };
    // eslint-disable-next-line no-console
    console.warn(`impact.failed for ${data.rootStepKey}:`, data.error);
  });

  const log = (kind: string, stepKey?: string, note?: string): void => {
    const entry = { ts: new Date().toISOString(), kind, stepKey, note };
    set({ cascadeLog: [...get().cascadeLog, entry] });
  };

  es.addEventListener('cascade.started', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as { order: string[] };
    set({ cascadeLog: [{ ts: new Date().toISOString(), kind: 'started', note: `${data.order.length} steps` }] });
  });
  es.addEventListener('cascade.rederiving', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as { stepKey: string };
    log('rederiving', data.stepKey);
  });
  es.addEventListener('cascade.skipped', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as { stepKey: string; reason: string };
    log('skipped', data.stepKey, data.reason);
  });
  es.addEventListener('cascade.failed', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as { stepKey: string; error: string };
    log('failed', data.stepKey, data.error);
  });
  es.addEventListener('cascade.done', () => log('done'));

  es.addEventListener('docs.published', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as {
      stepKey: string;
      provider: string;
      docs: import('./types').PublishedArtifactInfo[];
    };
    set((s) => ({
      publishedDocs: { ...s.publishedDocs, [data.stepKey]: data.docs },
    }));
  });

  es.addEventListener('comments.updated', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as {
      stepKey: string;
      comments: import('./types').ReviewComment[];
    };
    set((s) => ({
      stepComments: { ...s.stepComments, [data.stepKey]: data.comments },
    }));
  });

  es.addEventListener('step.pinned', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as { stepKey: string; pinned: boolean };
    mutateStep(set, get, data.stepKey, (s) => ({
      ...s,
      pinnedAtVersion: data.pinned ? s.version : undefined,
    }));
  });

  es.addEventListener('step.skipped', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as { stepKey: string };
    mutateStep(set, get, data.stepKey, (s) => ({
      ...s,
      status: 'skipped',
      pendingQuestions: undefined,
      pendingEdit: undefined,
      lastError: undefined,
      guardrailResults: undefined,
    }));
  });

  es.onerror = () => {
    if (es.readyState === EventSource.CLOSED) {
      // eslint-disable-next-line no-console
      console.error(
        `SSE stream closed for ${projectId}. Steps will remain "in-progress" until reconnected.`,
      );
    }
  };

  set({ stream: es });
}

function mutateStep(
  set: (partial: Partial<UIState> | ((s: UIState) => Partial<UIState>)) => void,
  get: () => UIState,
  stepKey: string,
  updater: (s: import('./types').Step) => import('./types').Step,
): void {
  const proj = get().project;
  if (!proj) return;
  set({
    project: {
      ...proj,
      phases: proj.phases.map((ph) => ({
        ...ph,
        steps: ph.steps.map((s) => (s.key === stepKey ? updater(s) : s)),
      })),
    },
  });
}

const _pollers = new Map<string, ReturnType<typeof setInterval>>();

/**
 * Backstop for SSE: poll the server every 2s until the step is no longer
 * in-progress. Gives up after 5 minutes. SSE is still the primary mechanism;
 * this only catches cases where events were dropped.
 */
function startStepPolling(
  projectId: string,
  stepKey: string,
  set: (partial: Partial<UIState> | ((s: UIState) => Partial<UIState>)) => void,
  get: () => UIState,
): void {
  const pollKey = `${projectId}:${stepKey}`;
  const existing = _pollers.get(pollKey);
  if (existing) clearInterval(existing);

  const startedAt = Date.now();
  const maxWaitMs = 5 * 60 * 1000;

  const tick = async (): Promise<void> => {
    if (Date.now() - startedAt > maxWaitMs) {
      const t = _pollers.get(pollKey);
      if (t) clearInterval(t);
      _pollers.delete(pollKey);
      return;
    }
    try {
      const { project: fresh } = await apiGetProject(projectId);
      const serverStep = fresh.phases.flatMap((p) => p.steps).find((s) => s.key === stepKey);
      if (!serverStep) return;
      // 'stale' is a transient state after submitEdit (server has saved
       // pendingEdit but executeStep hasn't started yet because impact
       // classification is still running). Treat it like 'in-progress'.
      if (
        serverStep.status === 'in-progress' ||
        serverStep.status === 'pending' ||
        serverStep.status === 'stale'
      ) return;
      // Server says done/failed/awaiting-input — sync local state and stop polling.
      mutateStep(set, get, stepKey, (s) => ({
        ...s,
        status: serverStep.status,
        artifacts: serverStep.artifacts,
        version: serverStep.version,
        lastError: serverStep.lastError,
        pendingQuestions: serverStep.pendingQuestions,
        pendingEdit: serverStep.pendingEdit,
        outdatedReason: serverStep.outdatedReason,
        guardrails: serverStep.guardrails ?? s.guardrails,
        guardrailResults: serverStep.guardrailResults,
      }));
      // Mirror lastError into stepErrors so the StepDetail error box renders
      // even when the SSE step.failed event was missed (stale listener, page
      // reload, etc.).
      if (serverStep.status === 'failed' && serverStep.lastError) {
        set({
          stepErrors: {
            ...get().stepErrors,
            [stepKey]: { message: serverStep.lastError.message, kind: serverStep.lastError.kind },
          },
        });
      }
      const t = _pollers.get(pollKey);
      if (t) clearInterval(t);
      _pollers.delete(pollKey);
    } catch {
      // ignore transient failures; next tick will retry
    }
  };

  const interval = setInterval(tick, 2000);
  _pollers.set(pollKey, interval);
  // Intentionally skip an immediate tick: the server needs a brief window to
  // mark the step in-progress in the DB. If we polled now, we'd read the
  // previous terminal state (e.g. `failed`) and yank the UI back, racing
  // executeStep before it has produced any SSE events.
}

/**
 * Initial values for every cross-session data field in the store. Called by
 * signIn / signOut / setSession to scrub whatever the previous tenant left
 * behind in memory. Anything that holds tenant-scoped data (projects,
 * assemblyLines, SSE buffers, per-step token pills, cascade logs) must be reset
 * here — leaving them in place is what makes one user "see" another's data
 * after a tab-local session swap.
 */
function freshDataState(): Partial<UIState> {
  return {
    project: null,
    activeStepVersions: {},
    projectRole: null,
    apps: [],
    activePhaseIdx: 0,
    expandedPhases: new Set<string>(),
    rpOpen: false,
    rpStepKey: null,
    fsOpen: false,
    fsArtifactIdx: 0,
    bootstrapping: false,
    bootstrapError: null,
    bootstrapWarning: null,
    stream: null,
    tokenPills: {},
    stepErrors: {},
    stepTimers: {},
    lastOutdated: null,
    cascadeLog: [],
    publishedDocs: {},
    stepComments: {},
    assemblyLines: [],
    selectedAssemblyLineId: null,
    editingAssemblyLine: null,
    importedDraft: null,
  };
}

/* ---------- derived selectors ---------- */

export function phaseStatus(phase: Phase): Status {
  let anyInProgress = false;
  let allDone = true;
  for (const s of phase.steps) {
    if (s.status === 'in-progress') anyInProgress = true;
    if (s.status !== 'done' && s.status !== 'skipped') allDone = false;
  }
  if (allDone) return 'done';
  if (anyInProgress) return 'in-progress';
  return 'pending';
}

/**
 * Pick a short display tag from a project's metadata. Falls back to the first
 * value if no well-known key is present, then to an empty string.
 */
export function tagFromMetadata(meta: Record<string, string> | undefined): string {
  if (!meta) return '';
  return meta.product ?? meta.platform ?? Object.values(meta)[0] ?? '';
}

export function countDone(phase: Phase): string {
  let done = 0;
  for (const s of phase.steps) {
    if (s.status === 'done' || s.status === 'skipped') done++;
  }
  return `${done}/${phase.steps.length}`;
}
